import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FirstPersonControls } from 'three/examples/jsm/controls/FirstPersonControls.js'; 
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js'; 
import { MapControls } from 'three/examples/jsm/controls/MapControls.js'; 
import GlobalConst from '@/core/GlobalConst.js'
import EventBus from './EventBus.js';
import gsap from 'gsap';

export default class CameraController {
  currentMode = null;
  currCamera = null;
  domElement = null;
  controls = null;
  animation = null;

  constructor(camera, domElement) {
    this.currCamera = camera;
    this.domElement = domElement;
  }

  switchMode(mode) {
    if (this.currentMode === mode) return;

    // 中断之前的动画
    if (this.animation) {
      this.animation.kill();
      this.animation = null;
    }

    // 销毁旧控制器
    if (this.controls) {
      this.controls.dispose();
      this.removeCameraChangeListeners();
    }

    // 关键：计算从 firstPerson 到 orbit 的平滑过渡起点
    let startTarget = new THREE.Vector3(0, 0, 0);
    if (this.currentMode!=null && this.currentMode != GlobalConst.CAMERA_CONTROLE_MODE_ORBIT) {
      // 1. 获取 firstPerson 相机的视线方向
      const lookDir = new THREE.Vector3();
      this.currCamera.getWorldDirection(lookDir);

      // 2. 计算虚拟目标点（相机前方 10 米处，可调整距离）
      const distance = 10;
      startTarget = this.currCamera.position.clone().add(lookDir.multiplyScalar(distance));

      // 3. 手动设置相机朝向虚拟目标点（避免控制器初始化时跳转）
      this.currCamera.lookAt(startTarget);
    }

    switch (mode) {
      case GlobalConst.CAMERA_CONTROLE_MODE_ORBIT: {
        // 4. 创建 OrbitControls，直接将 target 设为虚拟目标点
        this.controls = new OrbitControls(this.currCamera, this.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.target.copy(startTarget); // 关键：初始化目标点
        this.addOrbitControlsListeners();

        // 5. 从虚拟目标点动画到 (0,0,0)
        this.animation = gsap.to(this.controls.target, {
          x: 0,
          y: 0,
          z: 0,
          duration: 1.5,
          ease: "power2.inOut",
          onUpdate: () => {
            // 确保控制器实时更新相机朝向
            this.controls.update();
          },
          onComplete: () => {
            this.animation = null;
          }
        });

        break;
      }

      case GlobalConst.CAMERA_CONTROLE_MODE_FIRSTPERSON: {
        this.lastTime = 0;
        this.controls = new FirstPersonControls(this.currCamera, this.domElement);
        this.controls.movementSpeed = 5;
        this.controls.lookSpeed = 0.3;

        //this.controls.noFly = true; // 禁止飞行

        this.addFirstPersonControlsListeners();
        break;
      }

      case GlobalConst.CAMERA_CONTROLE_MODE_POINTERLOCK: {
        this.controls = new PointerLockControls(this.currCamera, document.body);
        this.addFirstPersonControlsListeners();
        // 尝试锁定鼠标
        this.controls.lock();
        break;
      }

      case GlobalConst.CAMERA_CONTROLE_MODE_MAP: {
        this.controls = new MapControls(this.currCamera, document.body);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.target.copy(startTarget); // 关键：初始化目标点
        this.addOrbitControlsListeners();
        break;
      }

      default:
        console.warn(`不支持的控制模式：${mode}`);
        return;
    }

    this.currentMode = mode;
    console.log(`相机控制模式已切换为：${mode}`);
  }

  // 其他方法保持不变...
  addOrbitControlsListeners() {
    this.controls.addEventListener('change', () => this.onCameraChange());
  }

  addFirstPersonControlsListeners() {
    this.lastCameraState = {
      position: this.currCamera.position.clone(),
      quaternion: this.currCamera.quaternion.clone(),
    };

    this.cameraChangeDetector = setInterval(() => {
      const currentPosition = this.currCamera.position;
      const currentQuaternion = this.currCamera.quaternion;

      const positionChanged = !currentPosition.equals(this.lastCameraState.position);
      const rotationChanged = !currentQuaternion.equals(this.lastCameraState.quaternion);

      if (positionChanged || rotationChanged) {
        this.onCameraChange();
        this.lastCameraState.position.copy(currentPosition);
        this.lastCameraState.quaternion.copy(currentQuaternion);
      }
    }, 16);
  }

  removeCameraChangeListeners() {
    // 1. 处理使用 'change' 事件的控制器 (OrbitControls, MapControls)
    if ((this.currentMode === GlobalConst.CAMERA_CONTROLE_MODE_ORBIT || this.currentMode === GlobalConst.CAMERA_CONTROLE_MODE_MAP) && this.controls) {
        // 移除 'change' 事件监听器
        this.controls.removeEventListener('change', () => this.onCameraChange);
    }
    // 2. 处理使用 setInterval 的控制器 (FirstPersonControls, PointerLockControls)
    else if ((this.currentMode === GlobalConst.CAMERA_CONTROLE_MODE_FIRSTPERSON || this.currentMode === GlobalConst.CAMERA_CONTROLE_MODE_POINTERLOCK) && this.cameraChangeDetector) {
        // 清除 interval
        clearInterval(this.cameraChangeDetector);
        this.cameraChangeDetector = null; // 重置引用，便于垃圾回收
    }
    if(this.currentMode === GlobalConst.CAMERA_CONTROLE_MODE_POINTERLOCK){
      this.controls.unlock();
    }
  }

  onCameraChange() {
    EventBus.emit('cameraChange', {
      position: this.currCamera.position.clone(),
      rotation: this.currCamera.rotation.clone(),
      quaternion: this.currCamera.quaternion.clone(),
      mode: this.currentMode,
    });
  }

  update() {
    if (this.currentMode == GlobalConst.CAMERA_CONTROLE_MODE_FIRSTPERSON) {
      let currentTime = performance.now();
      this.deltaTime = (currentTime - this.lastTime) / 1500;
      this.lastTime = currentTime;
    }

    if (this.controls && this.controls.update) {
      this.controls.update(this.deltaTime);
    }
  }

  dispose() {
    if (this.animation) {
      this.animation.kill();
    }
    this.removeCameraChangeListeners();
    if (this.controls) {
      this.controls.dispose();
    }
    this.currCamera = null;
    this.domElement = null;
    this.currentMode = null;
    this.controls = null;
  }
}